import { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { FieldArray } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';

import { KnmResultInfoTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmResultInfoTypeCodesEnum';
import { IKnmResultInfoType } from 'APIPackage/CatalogsModule/interfaces/IKnmResultInfoType';
import { getCatalogKnmResultInfoTypes } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { KNM_CANCELLATION_INFO } from 'CorePackage/MainModule/routes/routes';
import { getResultsCancellationInfoBlockId } from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmErpObjectResultSelectOption } from 'KnmErpPackage/MainModule/interfaces/IKnmErpObjectResultSelectOption';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import knmSharedStyles from 'KnmPackage/MainModule/styles/KnmShared.module.scss';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Section } from 'UIPackage/FormModule/components/Section';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { IKnmErpFormResultCancellationInfo } from '../interfaces/IKnmErpFormResultCancellationInfo';
import { KnmErpResultCancellationInfo } from './KnmErpResultCancellationInfo';

interface IKnmErpCancellationInfoProps {
  error?: string;
  knmObjectsResultsSelectOptions: IKnmErpObjectResultSelectOption[] | undefined;
  resultsCancellationInfo: IKnmForm['resultsCancellationInfo'];
  watchOnly?: boolean;
}

interface IKnmErpCancellationInfoStoreProps {
  formRef: IKnmFormRef | undefined;
  knmResultInfoTypes: IKnmResultInfoType[];
}

const KnmErpCancellationInfoWithoutStore = ({
  error,
  knmObjectsResultsSelectOptions,
  resultsCancellationInfo,
  watchOnly,
  formRef,
  knmResultInfoTypes,
}: IKnmErpCancellationInfoProps & IKnmErpCancellationInfoStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const knmResultCancellationInfoType: IKnmResultInfoType | undefined = useMemo(
    () =>
      knmResultInfoTypes.find(knmResultInfoType => knmResultInfoType.code === KnmResultInfoTypeCodesEnum.UNDOIG_SEC_I),
    [knmResultInfoTypes],
  );

  const addResultCancellationInfo = useCallback(
    () =>
      !watchOnly &&
      push &&
      push('resultsCancellationInfo', { type: knmResultCancellationInfoType, isNew: true, listId: uuidv4() }),
    [knmResultCancellationInfoType, push, watchOnly],
  );

  const makeRemoveResultCancellationInfoHandler = (index: number) => () =>
    !watchOnly && remove && remove('resultsCancellationInfo', index);

  return (
    <Section
      title="Сведения об отмене"
      id={KNM_CANCELLATION_INFO}
      type="list"
      customButton={
        <Button
          className="shared-btn-add"
          text="Добавить"
          type="button"
          variant="link"
          color="primary"
          disabled={watchOnly || Boolean(error) || !knmResultCancellationInfoType}
          onClick={addResultCancellationInfo}
        />
      }
    >
      {error && (
        <Row>
          <p className={knmSharedStyles.SectionErrorNotification}>{error}</p>
        </Row>
      )}

      <FieldArray name="resultsCancellationInfo">
        {({ fields }) => {
          if (!fields.length) {
            return null;
          }

          return fields.map((name, index) => {
            const fieldValue: IKnmErpFormResultCancellationInfo = fields.value?.[index];
            const id = getResultsCancellationInfoBlockId(fieldValue.listId);

            return (
              <KnmErpResultCancellationInfo
                key={id}
                id={id}
                index={index}
                knmObjectsResultsSelectOptions={knmObjectsResultsSelectOptions}
                name={name}
                resultsCancellationInfo={resultsCancellationInfo}
                value={fieldValue}
                watchOnly={watchOnly}
                onRemove={makeRemoveResultCancellationInfoHandler(index)}
              />
            );
          });
        }}
      </FieldArray>
    </Section>
  );
};

const mapStateToProps = (state: IRootState): IKnmErpCancellationInfoStoreProps => ({
  formRef: getKnmFormRef(state),
  knmResultInfoTypes: getCatalogKnmResultInfoTypes(state),
});

export const KnmErpCancellationInfo = connect(mapStateToProps)(KnmErpCancellationInfoWithoutStore);
